import { WebhookTransactionType } from "../stores/useWebhookStore";

export const APP_INACTIVITY_TIMEOUT = 300;

export const INT_BANK_FIELD_CONFIG: Record<
  string,
  { label: string; maxLength: number; isAlphaNumeric?: boolean }
> = {
  US: { label: "Routing No.", maxLength: 20 },
  CA: { label: "Routing No.", maxLength: 20 },
  EU: { label: "IBAN", maxLength: 34, isAlphaNumeric: true },
  GB: { label: "Sort Code", maxLength: 8 },
  IN: { label: "IFSC Code", maxLength: 11, isAlphaNumeric: true },
  default: { label: "Account No.", maxLength: 30 },
};

export const getBankFieldConfig = (iso2: string) =>
  INT_BANK_FIELD_CONFIG[iso2] || INT_BANK_FIELD_CONFIG.default;

export const getBankCodeMaxLength = (iso2: string): number => {
  return INT_BANK_FIELD_CONFIG[iso2]?.maxLength || INT_BANK_FIELD_CONFIG.default.maxLength;
};

export const INTERNATIONAL_BANK_ISO2 = new Set(["US", "GB", "CA", "EU", "IN"]);

export const MOMO_COUNTRIES_ISO2 = new Set(["BJ", "BF", "CI", "GW", "ML", "NE", "SN", "CM", "CD", "TZ", "RW", "UG", "ZM", "MW", "KE", "GH", "TG"]);

export enum PAYMENT_METHOD {
  WALLET = "WALLET",
  BANK_TRANSFER = "BANK_TRANSFER",
  ACH = "ACH",
  WIRE = "WIRE",
  MOBILE_MONEY = "MOBILE_MONEY",
  ZELLE = "ZELLE",
  INTERAC = "INTERAC",
  STABLE_CURRENCY = "STABLE_CURRENCY",
  WECHAT_PAY = "WECHAT_PAY",
  ALIPAY = "ALIPAY",
}

export enum BLOCKCHAIN_NETWORK {
  TRC20 = "TRC20",
  ERC20 = "ERC20",
  BEP20 = "BEP20",
  SOL = "SOL",
  TON = "TON",
  BASE = "BASE",
  POL = "POL",
  ARB = "ARB"
}

const paymentMethods = [
    "WALLET",
    "BANK_TRANSFER",
    "ACH",
    "WIRE",
    "MOBILE_MONEY",
    "ZELLE",
    "INTERAC",
    "STABLE_CURRENCY",
    "WECHAT_PAY",
    "ALIPAY"
,] as const;

export type selectMethodTypes = typeof paymentMethods[number];

export const EMAIL_PAYMENT_METHODS = new Set(["INTERAC", "ZELLE"]);

export const BANK_TRANSFER_METHODS = new Set<string>([
  PAYMENT_METHOD.BANK_TRANSFER,
  PAYMENT_METHOD.ACH,
  PAYMENT_METHOD.WIRE,
]);

export const isBankTransferMethod = (paymentMethod?: string): boolean =>
  !!paymentMethod && BANK_TRANSFER_METHODS.has(paymentMethod);

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  BANK_TRANSFER: "Bank Transfer",
  ACH: "ACH",
  WIRE: "Wire Transfer",
  MOBILE_MONEY: "Mobile Money",
  STABLE_CURRENCY: "Stable Coin",
  WALLET: "Wallet",
  ZELLE: "Zelle",
  INTERAC: "Interac",
  WECHAT_PAY: "WeChat Pay",
  ALIPAY: "Alipay",
};

export const GENERIC_BUSINESS_WORDS = [
  // Common global suffixes
  "ltd",
  "limited",
  "inc",
  "incorporated",
  "corp",
  "corporation",
  "company",
  "co",
  "plc",
  "llc",

  // Common business descriptors
  "enterprise",
  "enterprises",
  "ventures",
  "venture",
  "group",
  "holdings",
  "holding",
  "solutions",
  "services",
  "technologies",
  "technology",
  "systems",
  "global",
  "international",
  "industries",
  "industry",
  "partners",
  "trading",
]

export const BLOCKED_EMAIL_DOMAINS = new Set([
  // Global big ones
  "gmail.com", "googlemail.com",
  "gmail.co.uk", "gmail.fr", "gmail.de", "gmail.it", "gmail.es",
  "gmail.ca", "gmail.com.au", "gmail.in", "gmail.co.in",
  "gmail.co.jp", "gmail.com.br", "gmail.com.mx",

   "yahoo.com", "yahoo.co.uk", "yahoo.fr", "yahoo.de", "yahoo.it",
  "yahoo.es", "yahoo.ca", "yahoo.com.au", "yahoo.in", "yahoo.co.in",
  "yahoo.co.jp", "yahoo.com.br", "yahoo.com.mx",

   "hotmail.com", "hotmail.co.uk", "hotmail.fr", "hotmail.de", "hotmail.it",
  "hotmail.es", "hotmail.ca", "hotmail.com.au", "hotmail.in", "hotmail.co.in",
  "hotmail.co.jp", "hotmail.com.br", "hotmail.com.mx",

  "outlook.com", "outlook.co.uk", "outlook.fr", "outlook.de", "outlook.it",
  "outlook.es", "outlook.ca", "outlook.com.au", "outlook.in", "outlook.co.in",
  "outlook.co.jp", "outlook.com.br", "outlook.com.mx",

  "live.com", "live.co.uk", "live.fr", "live.de", "live.it",
  "live.es", "live.ca", "live.com.au", "live.in", "live.co.in",
  "live.co.jp", "live.com.br", "live.com.mx",

  "msn.com",
  "aol.com", "aol.co.uk", "aol.fr", "aol.de", "aol.it",
  "aol.es", "aol.ca", "aol.com.au", "aol.in", "aol.co.in",
  "aol.co.jp", "aol.com.br", "aol.com.mx",

  "icloud.com", "me.com", "mac.com",
  "protonmail.com", "proton.me",
  "zoho.com", "zohomail.com",
  "yandex.com", "yandex.ru",
  "mail.com", "email.com",
  "gmx.com", "gmx.net", "gmx.de",
  "fastmail.com",
  "hushmail.com",
  "tutanota.com", "tuta.io",

  // Europe
  "web.de",
  "orange.fr", "wanadoo.fr", "laposte.net",
  "libero.it", "virgilio.it", "alice.it",
  "seznam.cz",
  "o2.pl", "wp.pl", "interia.pl", "onet.pl",
  "mail.ru", "bk.ru", "inbox.ru", "list.ru",
  "ukr.net",

  // Asia
  "163.com", "126.com", "yeah.net",
  "sina.com", "sohu.com", "qq.com", "tom.com",
  "naver.com", "daum.net", "hanmail.net", "nate.com",
  "rediffmail.com", "indiatimes.com", "sify.com",
  "yahoo.co.jp",

  // Latin America
  "bol.com.br", "uol.com.br", "terra.com.br", "ig.com.br", "globo.com",
  "hotmail.com.mx", "yahoo.com.mx", "live.com.mx",

  // Africa
  "webmail.co.za", "vodamail.co.za",

  // Heavy-duty Disposable / Burner Domains
  "maildrop.cc",
  "yopmail.com", "yopmail.fr", "yopmail.net", "cool.fr.nf", "jetable.fr.nf",
  "mailinator.com", "mailinator.net", "mailinator2.com", "mailin8r.com", "testcorp100",
  "agentmail.to",
  "dispostable.com",
  "inboxbear.com",
  "jourrapide.com", "armyspy.com", "cuvox.de", "dayrep.com", "fleshmail.com", // Fake Name Generator infrastructure
  "guerrillamail.com", "guerrillamail.net", "guerrillamail.org", "guerrillamail.biz", "guerrillamailblock.com", "sharklasers.com",
  "temp-mail.org", "temp-mail.ru", "tempmail.altmails.com",
  "10minutemail.com", "10minutemail.co.uk", "10minutemail.net", "10minutesmail.com",
  "20minutemail.com",
  "33mail.com",
  "burnermc.com",
  "bheps.com",
  "trashmail.com", "trashmail.de", "trashmail.me",
  "getairmail.com",
  "mintemail.com",
  "mailnesia.com",
  "mailcatch.com",
  "mohmal.com",
  "moakt.com",
  "emailondeck.com",
  "eyepaste.com",
  "throwawaymail.com",
  "fakemailgenerator.com"
]);

export enum ORDER_STATUS  {
  PROCESSING = "PROCESSING",
  SUCCESSFUL = "SUCCESSFUL",
  PENDING = "PENDING",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED"
}

export enum PAYMENTLINK_STATUS  {
  PAID = "PAID",
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
  EXPIRED = "EXPIRED",
  DECLINED = "DECLINED"
}

export enum WEBHOOK_STATUS {
  FAILED = "FAILED",
  SUCCESSFUL = "SUCCESSFUL"
}

export enum UNIFIED_STATUS {
  CANCELLED = "CANCELLED",
  DECLINED = "DECLINED",
  EXPIRED = "EXPIRED",
  FAILED = "FAILED",
  PAID = "PAID",
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  SUCCESSFUL = "SUCCESSFUL",
}

export const currencyPaymentMethodMap: Record<string, "BANK_TRANSFER" | "MOBILE_MONEY"> = {
  NG: "BANK_TRANSFER",
  US: "BANK_TRANSFER",
  GB: "BANK_TRANSFER",
  CA: "BANK_TRANSFER",
  EU: "BANK_TRANSFER",
  BJ: "MOBILE_MONEY",
  BF: "MOBILE_MONEY",
  CI: "MOBILE_MONEY",
  GW: "MOBILE_MONEY",
  ML: "MOBILE_MONEY",
  NE: "MOBILE_MONEY",
  SN: "MOBILE_MONEY",
  CM: "MOBILE_MONEY",
  CD: "MOBILE_MONEY",
  TZ: "MOBILE_MONEY",
  RW: "MOBILE_MONEY",
  UG: "MOBILE_MONEY",
  ZM: "MOBILE_MONEY",
  MW: "MOBILE_MONEY",
  KE: "MOBILE_MONEY",
  GH: "MOBILE_MONEY",
  TG: "MOBILE_MONEY",

  CN: "BANK_TRANSFER",
  IN: "BANK_TRANSFER",
};

export const PAYMENT_METHOD_ARRAY = [
  "WALLET",
  "BANK_TRANSFER",
  "ACH",
  "WIRE",
  "MOBILE_MONEY",
  "STABLE_CURRENCY",
  "ZELLE",
  "INTERAC",
  "WECHAT_PAY",
  "ALIPAY"
];

export const WALLET_MOBILE_MONEY_CURRENCIES = [
  "GHS",
  "XOF",
  "XAF",
  "CDF",
  "UGX",
  "TZS",
  "RWF",
  "ZMW",
  "MWK",
  "KES",
  "ETB"
]

export type SupportedISO2 = "CD" | "SN" | "BJ" | "CM" | "GH" | "BF" | "CI" | "ET" | "KE" | "MW" | "RW" | "UG" | "TZ" | "ZM";

export const MOBILE_LENGTH_BY_ISO2: Record<SupportedISO2, number> = {
  CD: 9,
  SN: 9,
  BJ: 10,
  CM: 9,
  GH: 9,
  BF: 8,
  CI: 10,
  ET: 9,
  KE: 9,
  MW: 9,
  RW: 9,
  UG: 9,
  TZ: 9,
  ZM: 9,
};

export enum CONFIG_TYPE  {
  WITHDRAWAL_FEE = "WITHDRAWAL_FEE",
  DEPOSIT_FEE = "DEPOSIT_FEE",
}


export const WEBHOOK_EVENT_TYPE_MAP: Record<string, WebhookTransactionType> = {
  'exchange': 'exchange',
  'payment_link': 'payment_link',
  'wallet.created': 'wallet_creation',
  'wallet.virtual_account': 'wallet_virtual_account',
  'wallet.transaction': 'wallet_txn',
  'stable_wallet.created': 'stable_wallet_creation',
  'stable_wallet.transaction': 'stable_wallet_txn',
};